import { useEffect, useState, useRef, useMemo } from 'react';
import './MotionActivity.less';
import { useSelector, useDispatch } from 'react-redux';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  MotionActivityIcon2,
  NewHC200Icon,
  ArrowRightIcon,
  ArrowLeftIcon,
  MorningIcon,
  NightIcon,
  MotionEmpty,
  BatteryHighIcon,
  BatteryMediumIcon,
  BatteryLowIcon,
  BatteryZeroIcon,
  ArrowUpMotion,
  ArrowDownMotion,
} from '../Common/Icons';
import { Spin, Switch, Tooltip, message } from 'antd';
import MotionCalendar from './MotionCalendar';
import { DefaultTimezone } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import moment from 'moment';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import {
  get_utc_offset,
  processMotionData,
  sortByModelAndLocation,
  dayORNight,
  convertTo24HourFormat,
  timeArray,
  processHistMotionData,
  processPastEnvironmentalData,
  MINIMUM_MOTION_COUNT_PERHOUR,
} from './MotionActivityUtils';
import {
  setHistoricalData,
  selectHistoricalData,
} from '@vantiva-sa/homesight_humaui/Store/historicalDataStore';
import MotionLineChart from './MotionLineChart';
import MOTION_RANGES from '../../../motionRanges.json';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { getTimeZoneFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { getUserConfig } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getCurrentLanguage } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getTranslatedLocationName } from '../DeviceInventory/DeviceInventoryList';
import useGetUserAccessories from '@/Components/Hooks/useGetUserAccessories';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Access, checkRoleAccess } from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';

dayjs.extend(utc);
dayjs.extend(timezone);

const MotionActivity = ({
  hubRecord,
  timezone,
  deviceStatus,
  loginUserTimeformat,
  isTrend = null,
  openSubMenu,
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataLoadedQuarter, setDataLoadedQuarter] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  const [shiftDate, setShiftDate] = useState(null);
  const [dataPerHour, setDataPerHour] = useState(null);
  const [dataPerQuarter, setDataPerQuarter] = useState(null);
  const [pastDataPerHour, setPastDataPerHour] = useState(null);
  const [pastDataPerQuarter, setPastDataPerQuarter] = useState(null);
  const [pastDataLoaded, setPastDataLoaded] = useState(false);
  const [pastDataLoadedQuarter, setPastDataLoadedQuarter] = useState(false);
  const [hideLeftArrow, setHideLeftArrow] = useState(false);
  const [hideRightArrow, setHideRightArrow] = useState(true);
  const [allDevicesFetched, setAllDevicesFetched] = useState({});
  const [showPastDataDevice, setShowPastDataDevice] = useState([]);
  const [trendViewByDevice, setTrendViewByDevice] = useState({});
  const [lightData, setLightData] = useState({});
  const calendarRef = useRef(null);
  const currentUser = useSelector(getCurrentUser);
  const { useruuid } = useMemo(() => currentUser, [currentUser]);
  const currentUserConfig = useSelector(getUserConfig(useruuid));
  const userData = useSelector((user) => user.data.userInfo);
  const currentUserDataInfo = userData[openSubMenu ? hubRecord?.useruuid : useruuid];
  const userTimezone = getTimeZoneFormat(currentUserDataInfo?.timezone);
  const [dateToDisplay, setDateToDisplay] = useState('');
  const [activityDateStart, setActivityDateStart] = useState('');
  const [activityDateEnd, setActivityDateEnd] = useState('');

  const timezoneOffset = useMemo(() => get_utc_offset(timezone, DefaultTimezone), [timezone]);
  const allowShowTrend = useMemo(() => checkRoleAccess(Access.beta.labsPreview), []);

  useEffect(() => {
    if (userTimezone) {
      const currentDate = dayjs().tz(userTimezone);
      setDateToDisplay(currentDate.format('ddd MMM DD YYYY'));
      setActivityDateStart(moment().tz(userTimezone).startOf('day').format());
      setActivityDateEnd(moment().tz(userTimezone).endOf('day').format());
      setInitialDataLoaded(true);
    }
  }, [userTimezone]);

  const {
    SMALL_RANGE_MIN = 0,
    SMALL_RANGE_MAX = 50,
    SMALL_RANGE_VALUE = 8,
    MEDIUM_RANGE_MIN = 50,
    MEDIUM_RANGE_MAX = 100,
    MEDIUM_RANGE_VALUE = 13,
    LARGE_RANGE_MIN = 100,
    LARGE_RANGE_MAX = 200,
    LARGE_RANGE_VALUE = 18,
    XLARGE_RANGE_MIN = 200,
    XLARGE_RANGE_VALUE = 28,
  } = MOTION_RANGES || {};
  // State variables for different ranges
  const [smallRangeMin, setSmallRangeMin] = useState(SMALL_RANGE_MIN);
  const [smallRangeMax, setSmallRangeMax] = useState(SMALL_RANGE_MAX);
  const [smallRangeVal, setSmallRangeVal] = useState(SMALL_RANGE_VALUE);
  const [mediumRangeMin, setMediumRangeMin] = useState(MEDIUM_RANGE_MIN);
  const [mediumRangeMax, setMediumRangeMax] = useState(MEDIUM_RANGE_MAX);
  const [mediumRangeVal, setMediumRangeVal] = useState(MEDIUM_RANGE_VALUE);
  const [largeRangeMin, setLargeRangeMin] = useState(LARGE_RANGE_MIN);
  const [largeRangeMax, setLargeRangeMax] = useState(LARGE_RANGE_MAX);
  const [largeRangeVal, setLargeRangeVal] = useState(LARGE_RANGE_VALUE);
  const [xLargeRangeMin, setXLargeRangeMin] = useState(XLARGE_RANGE_MIN);
  const [xLargeRangeVal, setXLargeRangeVal] = useState(XLARGE_RANGE_VALUE);
  const currentUserDataSet = useSelector((user) => user.data.userInfo);
  const currentPrincipleUserDataInfo = currentUserDataSet[hubRecord?.useruuid];
  const principleUserTimezone = getTimeZoneFormat(currentPrincipleUserDataInfo.timezone);

  useEffect(() => {
    const userConfig = currentUserConfig?.portal_motion_bubble;
    if (userConfig) {
      // Using function to batch updates
      setSmallRangeMin((prev) => Number(userConfig?.small?.min ?? prev));
      setSmallRangeMax((prev) => Number(userConfig?.small?.max ?? prev));
      setSmallRangeVal((prev) => Number(userConfig?.small?.circle_radius ?? prev));

      setMediumRangeMin((prev) => Number(userConfig?.medium?.min ?? prev));
      setMediumRangeMax((prev) => Number(userConfig?.medium?.max ?? prev));
      setMediumRangeVal((prev) => Number(userConfig?.medium?.circle_radius ?? prev));

      setLargeRangeMin((prev) => Number(userConfig?.large?.min ?? prev));
      setLargeRangeMax((prev) => Number(userConfig?.large?.max ?? prev));
      setLargeRangeVal((prev) => Number(userConfig?.large?.circle_radius ?? prev));

      setXLargeRangeMin((prev) => Number(userConfig?.xlarge?.min ?? prev));
      setXLargeRangeVal((prev) => Number(userConfig?.xlarge?.circle_radius ?? prev));
    }
  }, [currentUserConfig, useruuid]);

  const motionBubble = (occ) => {
    if (occ > smallRangeMin && occ < smallRangeMax)
      return smallRangeVal; // small
    else if (occ >= mediumRangeMin && occ < mediumRangeMax)
      return mediumRangeVal; // medium
    else if (occ >= largeRangeMin && occ < largeRangeMax)
      return largeRangeVal; // large
    else if (occ >= xLargeRangeMin) return xLargeRangeVal; // x-large
  };

  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const trans = useSelector(translator);
  const dispatch = useDispatch();
  const { data: accessoriesData = [], isFetched: accessoriesFetched } =
    useGetUserAccessories(hubRecord);

  const storedDataSelector = (state, useruuid) => {
    const historicalData = {};
    const daysToFetch = [1, 2, 3, 4, 5, 6];
    daysToFetch.forEach((i) => {
      const date = moment(dateToDisplay).subtract(i, 'days').format('YYYY-MM-DD');
      if (!historicalData[useruuid]) {
        historicalData[useruuid] = {};
      }
      historicalData[useruuid][date] = selectHistoricalData(state, useruuid, date);
    });
    return historicalData;
  };

  const storedData = useSelector((state) => storedDataSelector(state, hubRecord.useruuid));

  const fetchMotionData = () => {
    setDataLoaded(false);
    const applyTimezoneOffset = (date) => date.replace(/Z|(\+|-)\d\d:\d\d$/, timezoneOffset);
    const activityDateStartWithOffset = applyTimezoneOffset(activityDateStart);
    const activityDateEndWithOffset = applyTimezoneOffset(activityDateEnd);

    const errorCallback = (error) => {
      console.error('Error fetching motion data:', error);
    };
    const successCallback = (data) => {
      processMotionData(
        data,
        dateToDisplay,
        timezone,
        timezoneOffset,
        allDevicesFetched,
        setDataPerHour,
        setDataLoaded,
        setDataPerQuarter,
        setDataLoadedQuarter,
        loginUserTimeformat
      );
    };

    try {
      userEventsApi.userMotionEventsByTimeRange(
        hubRecord.useruuid,
        activityDateStartWithOffset,
        activityDateEndWithOffset,
        {},
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const fetchHistoricalDataForPastSixDays = async (storedData, key) => {
    const useruuid = hubRecord.useruuid;
    const historicalData = {};
    const applyTimezoneOffset = (date) => date.replace(/Z|(\+|-)\d\d:\d\d$/, timezoneOffset);
    const daysToFetch = [1, 2, 3, 4, 5, 6];
    const promises = [];

    for (const i of daysToFetch) {
      const date = moment(dateToDisplay).subtract(i, 'days').format('YYYY-MM-DD');

      if (storedData[useruuid] && storedData[useruuid][date]) {
        historicalData[date] = storedData[useruuid][date];
        continue;
      }

      const activityDateStart = moment(date).startOf('day').format();
      const activityDateEnd = moment(date).endOf('day').format();

      const activityDateStartWithOffset = applyTimezoneOffset(activityDateStart);
      const activityDateEndWithOffset = applyTimezoneOffset(activityDateEnd);

      const errorCallback = (error) => {
        console.error(`Error fetching data for ${date}:`, error);
      };

      const successCallback = (data) => {
        try {
          historicalData[date] = data;
          dispatch(setHistoricalData({ useruuid: hubRecord.useruuid, date, data }));
        } catch (parseError) {
          console.error(`Error parsing data for ${date}:`, parseError);
        }
      };

      const fetchPromise = new Promise((resolve, reject) => {
        try {
          userEventsApi.userDeviceEventsByTypesAndTimeRange(
            hubRecord.useruuid,
            activityDateStartWithOffset,
            activityDateEndWithOffset,
            {
              deviceuuid: Object.keys(allDevicesFetched),
              eventtypes: ['motion_detected'],
            },
            apiCallback({
              translator: trans,
              failCallback: (error) => {
                errorCallback(error);
                reject(error);
              },
              errorCallback: (error) => {
                errorCallback(error);
                reject(error);
              },
              successCallback: (data) => {
                successCallback(data);
                resolve(data);
              },
            })
          );
        } catch (error) {
          errorCallback(error);
          reject(error);
        }
      });

      promises.push(fetchPromise);
    }

    await Promise.all(promises);
    return historicalData;
  };

  useEffect(() => {
    if (Object.keys(allDevicesFetched).length) {
      isTrend && fetchPastEnvironmentalData();
    }
  }, [isTrend, dateToDisplay, allDevicesFetched]);

  const fetchPastEnvironmentalData = () => {
    const applyTimezoneOffset = (date) => date.replace(/Z|(\+|-)\d\d:\d\d$/, timezoneOffset);
    const activityDateStartWithOffset = applyTimezoneOffset(activityDateStart);
    const activityDateEndWithOffset = applyTimezoneOffset(activityDateEnd);

    const errorCallback = (error) => {
      console.error('Error fetching past environmental data:', error);
    };
    const successCallback = (data) => {
      processPastEnvironmentalData(data, dateToDisplay, timezone, timezoneOffset, setLightData);
    };

    try {
      userEventsApi.userDeviceEventsByTypesAndTimeRange(
        hubRecord.useruuid,
        activityDateStartWithOffset,
        activityDateEndWithOffset,
        {
          deviceuuid: Object.keys(allDevicesFetched), //["5a3b04b4-347b-4358-ba1d-66538a5154cd"],
          eventtypes: ['environment_status'],
        },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const renderMotionAsPercent = (
    deviceModel,
    hoursIn24Format,
    dataPerHour,
    dataPerQuarter,
    isTrend
  ) => {
    if (dataPerHour == null) return;
    var visible = isTrend ? 'hidden' : '';
    const motionCount = dataPerHour[hoursIn24Format] || 0;
    const shouldHideBubble = deviceModel === 'HC200' && motionCount <= MINIMUM_MOTION_COUNT_PERHOUR;

    const radiusBubble = dataPerHour.hasOwnProperty(hoursIn24Format)
      ? motionBubble(motionCount)
      : (visible = 'hidden');

    const commonStyles = {
      border: '0px',
      borderRadius:
        hoursIn24Format === '0'
          ? '5px 0px 0px 5px'
          : hoursIn24Format === '23'
            ? '0px 5px 5px 0px'
            : '',
    };
    // ---- pull quarter values for this hour (q0..q3) ----
    const qBucket = dataPerQuarter?.[Number(hoursIn24Format)] || {};
    const qKeys = ['q0', 'q1', 'q2', 'q3'];
    const qVals = qKeys.map((k) => Number(qBucket?.[k] ?? 0));
    const totalQuarter = qVals.reduce((a, b) => a + b, 0);

    // Option A (use your existing bubble sizing semantics):
    const widthFromCount = (val) => `${Math.max(2, Math.round((motionBubble(val) || 0) / 2))}px`;

    const colorFor = (val) => {
      if (val <= 2) return 'transparent'; // no visible bar
      if (val <= 50) return '#FFD6A5'; // light orange
      if (val <= 100) return '#FFB347'; // medium orange
      if (val <= 150) return '#FF8C00'; // dark orange
      return '#E65100'; // deep orange (150+)
    };

    return visible === 'hidden' ? (
      <div
        className="motion-empty-block"
        style={isTrend ? { ...commonStyles, height: '64px' } : commonStyles}
      >
        {isTrend ? null : <MotionEmpty style={{ margin: '6px' }} />}
      </div>
    ) : (
      <div
        className="motion-percent-block"
        style={{
          ...commonStyles,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '40px',
        }}
      >
        {qKeys.map((q, i) => {
          const v = qVals[i];
          return (
            <div
              key={q}
              className={`quarter-line ${q} ${v > 0 ? 'active' : ''}`}
              style={{
                width: widthFromCount(v),
                background: v > 0 ? colorFor(v) : 'transparent',
                transition: 'width 0.2s ease',
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderMorningNightIcon = (hour) => {
    const icon = hour === '6 AM' ? <MorningIcon /> : hour === '6 PM' ? <NightIcon /> : null;

    return <div style={{ width: '12px', height: '16px' }}>{icon}</div>;
  };

  const lastHourEntry = useMemo(() => {
    if (!dataPerHour || Object.keys(dataPerHour).length === 0) return -1;

    const [{ hourlyData = {} } = {}] = Object.values(dataPerHour);
    const keys = Object.keys(hourlyData?.hourlyData);

    if (!keys.length) return moment.tz(principleUserTimezone).format('H');
    const lastKey = parseInt(keys[keys.length - 1], 10);
    return isNaN(lastKey) ? -1 : lastKey;
  }, [dataPerHour]);

  const renderHours = (
    deviceModel = null,
    hour,
    dataPerHour,
    timeRanges,
    dataPerQuarter,
    activityRangesHour,
    lastHourEntry,
    type = null,
    lightData = {},
    deviceUuid = null
  ) => {
    const hoursIn24Format = convertTo24HourFormat(hour);

    const currentDay = moment.tz(timezone.split(' ')[0]).format('YYYY-MM-DD');
    const displayDay = moment(dateToDisplay).format('YYYY-MM-DD');

    const isToday = type === 'past' ? false : currentDay === displayDay;
    const isLaterHour = isToday && parseInt(hoursIn24Format) > lastHourEntry;

    const hasZeroData =
      dataPerHour.hasOwnProperty(hoursIn24Format) && dataPerHour[hoursIn24Format] === 0;

    const timeOfDay =
      hoursIn24Format >= 6 && hoursIn24Format < 12
        ? trans('MORNING')
        : hoursIn24Format >= 12 && hoursIn24Format < 18
          ? trans('AFTERNOON')
          : hoursIn24Format >= 18 && hoursIn24Format < 24
            ? trans('EVENING')
            : trans('NIGHT');
    const timeRange =
      timeRanges && timeRanges[hoursIn24Format]
        ? `${timeRanges[hoursIn24Format].firstReportedTime} - ${timeRanges[hoursIn24Format].lastReportedTime}`
        : 'N/A';
    const event_value = dataPerHour.hasOwnProperty(hoursIn24Format)
      ? dataPerHour[hoursIn24Format]
      : '--';
    const hourKey = Number(hoursIn24Format);
    const firstMotion = activityRangesHour?.[hourKey]?.firstMotionTime ?? '—';
    const lastMotion = activityRangesHour?.[hourKey]?.lastMotionTime ?? '—';

    const activity_value =
      event_value === '--'
        ? '--'
        : event_value === 0
          ? trans('NONE')
          : event_value > smallRangeMin && event_value < smallRangeMax
            ? trans('LOW')
            : event_value >= mediumRangeMin && event_value < mediumRangeMax
              ? trans('MEDIUM')
              : event_value >= largeRangeMin && event_value < largeRangeMax
                ? trans('HIGH')
                : trans('VERYHIGH');
    const tooltipContent = (
      <div className="tooltip-content">
        <div className="tooltip-info">
          <div className="tooltip-label">
            <div>{timeOfDay}</div>
            <div>{timeRange}</div>
          </div>
          <div className="tooltip-activity">
            <div>
              {trans('ACTIVITY:')} {activity_value}
            </div>
            <div>
              {trans('EVENTS:')} {event_value}
            </div>
            <div>
              {trans('FIRST_MOTION')}: {firstMotion}
            </div>
            <div>
              {trans('LAST_MOTION')}: {lastMotion}
            </div>
            {isTrend && (
              <div>
                {`${trans('AMBIENTLIGHT')}:`} {lightData[hoursIn24Format]}
              </div>
            )}
          </div>
        </div>
      </div>
    );

    return (
      <div key={hour}>
        <div className={`main-hour-container ${isTrend ? 'trend' : 'not-trend'}`}>
          {type !== 'past' && (
            <>
              <div className="day-night-theme">{renderMorningNightIcon(hour)}</div>
              <div className="day-night-time">
                {loginUserTimeformat && loginUserTimeformat?.is24hoursformat
                  ? convertTo24HourFormat(hour, loginUserTimeformat?.is24hoursformat)
                  : (() => {
                      if (
                        hour === '12 AM' ||
                        hour === '6 AM' ||
                        hour === '12 PM' ||
                        hour === '6 PM'
                      ) {
                        return hour;
                      } else {
                        return hour.replace(/AM|PM/, '');
                      }
                    })()}
              </div>
            </>
          )}
          {!isLaterHour && (
            <Tooltip title={tooltipContent} placement="bottom" overlayClassName="custom-tooltip">
              <div
                className={`
                  ${dayORNight(hour) === 'night' ? 'night-time-block' : 'day-time-block'}
                  ${isTrend ? 'trend' : 'not-trend'}
                  ${hour === '12 AM' ? 'start' : hour === '11 PM' ? 'end' : ''}
                `}
              >
                {isTrend ? (
                  <div className="motion-activity-block">
                    {renderMotionAsPercent(
                      deviceModel,
                      hoursIn24Format,
                      dataPerHour,
                      dataPerQuarter,
                      isTrend
                    )}
                  </div>
                ) : (
                  <div className="motion-activity-block">
                    {!hasZeroData
                      ? renderMotionAsPercent(
                          deviceModel,
                          hoursIn24Format,
                          dataPerHour,
                          dataPerQuarter
                        )
                      : null}
                  </div>
                )}
              </div>
            </Tooltip>
          )}
          {isLaterHour && (
            <div
              className={`
              future-hour-block
              ${isTrend ? 'trend' : 'not-trend'}
              ${hour === '12 AM' ? 'start' : hour === '11 PM' ? 'end' : ''}
            `}
            ></div>
          )}
        </div>
      </div>
    );
  };

  const renderSevenDaysData = async (key) => {
    const histData = await fetchHistoricalDataForPastSixDays(storedData, key);
    processHistMotionData(
      histData,
      timezone,
      timezoneOffset,
      allDevicesFetched,
      setPastDataPerHour,
      setPastDataLoaded,
      setPastDataPerQuarter,
      setPastDataLoadedQuarter,
      loginUserTimeformat
    );
  };

  const renderActivityItem = (key) => {
    const deviceKey = key;
    const item = {
      ...dataPerHour[deviceKey],
      quarterlyData: dataPerQuarter?.[deviceKey]?.quarterlyData,
    };
    const isExpanded = showPastDataDevice.includes(deviceKey);
    const showTrendChart = Boolean(trendViewByDevice[deviceKey]);

    return (
      <div
        className={`main-activity-container ${isExpanded ? 'expanded' : 'collapsed'}`}
        key={deviceKey}
      >
        {renderDeviceIcon(
          item.serialNumber,
          item.location,
          item.model,
          item.connectionStatus,
          item.batteryLevel,
          deviceKey,
          showTrendChart
        )}
        <div
          className="motion-activity-container"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {(!isExpanded || !showTrendChart) && renderMotionActivity(item)}
          <Spin spinning={isExpanded && !pastDataLoaded} className="spin-margin">
            {isExpanded &&
              pastDataLoaded &&
              (showTrendChart ? (
                renderPastWeekLineChart(deviceKey)
              ) : (
                <>
                  {Object.keys(pastDataPerHour || {})
                    .sort((a, b) => new Date(b) - new Date(a))
                    .map((dateKey) => {
                      if (pastDataPerHour?.[dateKey]?.[deviceKey]) {
                        return renderPastMotionData(dateKey, deviceKey);
                      }
                      return null;
                    })}
                </>
              ))}
          </Spin>
        </div>

        {!isTrend && (
          <div className="past-render-btn">
            <div className="btn-text" onClick={() => handleClickPastDataToggle(deviceKey)}>
              {isExpanded ? (
                <>
                  <ArrowUpMotion />
                  <div>{trans('VIEWTODAYONLY')}</div>
                </>
              ) : (
                <>
                  <ArrowDownMotion />
                  <div>{trans('VIEWPASTWEEK')}</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderActivityItemTrend = (key) => {
    const item = dataPerHour[key];
    if (item.model !== 'EM200') return null;
    return (
      <div
        className="main-activity-container"
        key={key}
        style={{ minWidth: '1135px', height: '220px' }}
      >
        {renderDeviceIcon(
          item.serialNumber,
          item.location,
          item.model,
          item.connectionStatus,
          item.batteryLevel
        )}
        <div className="motion-activity-container">
          <div className="trend-legends">
            <div className="motion-legend">{trans('MOTION')}</div>
            <div className="ambient-light-legend">{trans('Ambient Light')}</div>
          </div>
          {renderMotionActivityTrend(item, key)}
        </div>
      </div>
    );
  };

  const DeviceInfoContainer = ({
    deviceLocation,
    keyProp,
    serialNum,
    children,
    showTrendChart,
  }) => {
    const currentLanguage = useSelector(getCurrentLanguage);
    const translatedLocation = getTranslatedLocationName(
      deviceLocation,
      currentUserConfig,
      currentLanguage
    );
    const formattedDate = moment(dateToDisplay).format('dddd MMMM D');
    const formattedStartDate = moment(dateToDisplay).subtract(6, 'days').format('dddd MMMM D');
    const formattedEndDate = formattedDate;

    const [day, month, date] = formattedDate.split(' ');
    const [startDay, startMonth, startDate] = formattedStartDate.split(' ');
    const [endDay, endMonth, endDate] = formattedEndDate.split(' ');

    return (
      <div className="device-info-container">
        <div className="device-id-info">
          <div className="device-info-header">{translatedLocation}</div>
          <div className="device-serial-number">{serialNum}</div>
        </div>
        <div className="device-icon">{children}</div>
        <div className="date-range">
          {showPastDataDevice.includes(keyProp) ? (
            <span>
              <span className="day-bold">{trans(startDay.toUpperCase())}</span>{' '}
              {trans(startMonth.toUpperCase())} {startDate} to{' '}
              <span className="day-bold">{trans(endDay.toUpperCase())}</span>{' '}
              {trans(endMonth.toUpperCase())} {endDate}
            </span>
          ) : (
            <span>
              <span className="day-bold">{trans(day.toUpperCase())}</span>{' '}
              {trans(month.toUpperCase())} {date}
            </span>
          )}
        </div>
        {allowShowTrend && (
          <div className="flex-align-center" style={{ height: '100%' }}>
            {showPastDataDevice.includes(keyProp) && (
              <div className="trend-switch-container">
                <span className="trend-switch-label">{trans('TREND') || 'Trend'}</span>
                <Switch
                  checked={showTrendChart}
                  onChange={(checked) =>
                    setTrendViewByDevice((prev) => ({
                      ...prev,
                      [keyProp]: checked,
                    }))
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDeviceIcon = (
    serialNumber = null,
    deviceLocation,
    deviceModel,
    connectionStatus,
    batteryLevel,
    key,
    showTrendChart = false
  ) => {
    let icon;
    if (deviceModel === 'HC200') {
      icon = <NewHC200Icon status={deviceStatus} className="icon" />;
    } else {
      if (!batteryLevel) {
        icon = <BatteryZeroIcon connectionStatus={connectionStatus} />;
      } else if (batteryLevel < 35) {
        icon = <BatteryLowIcon connectionStatus={connectionStatus} />;
      } else if (batteryLevel <= 49) {
        icon = <BatteryMediumIcon connectionStatus={connectionStatus} />;
      } else {
        icon = <BatteryHighIcon connectionStatus={connectionStatus} />;
      }
    }

    return (
      <DeviceInfoContainer
        deviceLocation={deviceLocation}
        keyProp={key}
        serialNum={serialNumber}
        showTrendChart={showTrendChart}
      >
        {icon}
      </DeviceInfoContainer>
    );
  };

  const translatedPastMotionTitle = (dateString) => {
    const translatedMonth = trans(moment(dateString).format('ddd').toUpperCase());
    return `${translatedMonth} ${moment(dateString).format('M/D')}`;
  };

  const formatHourRangeLabel = (hour, is24HourFormat) => {
    const startHour = hour;
    const endHour = (hour + 1) % 24;

    if (is24HourFormat) {
      const format24Hour = (value) => `${value.toString().padStart(2, '0')}:00`;
      return `${format24Hour(startHour)}-${format24Hour(endHour)}`;
    }

    return `${moment({ hour: startHour }).format('hA')}-${moment({ hour: endHour }).format('hA')}`;
  };

  const buildPastWeekLineChartData = (deviceKey) => {
    const hourLabels = Array.from({ length: 24 }, (_, hour) =>
      formatHourRangeLabel(hour, loginUserTimeformat?.is24hoursformat)
    );

    const currentDay = moment.tz(timezone.split(' ')[0]).format('YYYY-MM-DD');
    const displayDay = moment(dateToDisplay).format('YYYY-MM-DD');
    const isToday = currentDay === displayDay;

    const dayOffsets = [6, 5, 4, 3, 2, 1, 0];
    const dayColors = ['#6B7280', '#0EA5E9', '#14B8A6', '#10B981', '#F59E0B', '#F97316', '#005C98'];

    const datasets = dayOffsets
      .map((offset, index) => {
        const dateKey = moment(dateToDisplay).subtract(offset, 'days').format('YYYY-MM-DD');
        const currentDayData = dataPerHour?.[deviceKey]?.hourlyData?.hourlyData;
        const pastDayData = pastDataPerHour?.[dateKey]?.[deviceKey]?.hourlyData?.hourlyData;
        const dayHourlyData = offset === 0 ? currentDayData : pastDayData;

        const lineData = Array.from({ length: 24 }, (_, hour) => {
          const isLaterHour = isToday && offset === 0 && hour > Number(lastHourEntry);

          // Match heat-map behavior: hide future hours on current day.
          if (isLaterHour) {
            return null;
          }

          return Number(dayHourlyData?.[hour] ?? 0);
        });

        return {
          label: translatedPastMotionTitle(dateKey),
          data: lineData,
          borderColor: dayColors[index],
          backgroundColor: dayColors[index],
          borderWidth: offset === 0 ? 3 : 2,
          tension: 0.3,
          pointRadius: 1.5,
          pointHoverRadius: 4,
          datalabels: {
            display: false,
          },
        };
      })
      .filter(Boolean);

    return {
      labels: hourLabels,
      datasets,
    };
  };

  const getPastWeekLineChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 30,
          boxHeight: 2,
          usePointStyle: true,
          pointStyle: 'line',
          pointStyleWidth: 25,
          padding: 14,
        },
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: trans('HOURS') || 'Hours',
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
        },
        grid: {
          color: 'rgba(17, 62, 87, 0.12)',
        },
      },
      y: {
        beginAtZero: true,
        grace: '2%',
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: trans('EVENTS') || 'Motion Count',
        },
        grid: {
          color: 'rgba(17, 62, 87, 0.12)',
        },
      },
    },
  });

  const renderPastWeekLineChart = (deviceKey) => {
    const chartData = buildPastWeekLineChartData(deviceKey);
    return (
      <div className="weekly-motion-line-chart">
        <Line data={chartData} options={getPastWeekLineChartOptions()} />
      </div>
    );
  };

  const renderMotionActivity = (deviceData) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', marginLeft: 'auto' }}>
        <div className="past-motion-container" style={{ marginTop: '30px' }}>
          {translatedPastMotionTitle(dateToDisplay)}
        </div>
        {timeArray.map((hour) =>
          renderHours(
            deviceData.model,
            hour,
            deviceData.hourlyData?.hourlyData,
            deviceData.hourlyData?.timeRanges,
            deviceData?.quarterlyData?.quarterlyData,
            deviceData?.quarterlyData?.activityRangesHour,
            lastHourEntry,
            'current',
            {},
            deviceData.serialNumber
          )
        )}
      </div>
    );
  };

  const renderMotionActivityTrend = (deviceData, deviceUuid) => {
    return (
      <div className="motion-trend-container">
        <div className="motion-trend-hours">
          {timeArray.map((hour) =>
            renderHours(
              deviceData.model,
              hour,
              deviceData.hourlyData?.hourlyData,
              deviceData.hourlyData?.timeRanges,
              deviceData?.quarterlyData?.quarterlyData,
              deviceData?.quarterlyData?.activityRangesHour,
              lastHourEntry,
              null,
              lightData[deviceUuid],
              deviceUuid
            )
          )}
        </div>
        <div className="motion-trend-graph">
          {Object.keys(lightData).length > 0 && (
            <MotionLineChart
              dataPerHourArray={[deviceData.hourlyData.hourlyData, lightData[deviceUuid]]}
              colors={['steelblue', 'black']}
            />
          )}
        </div>
      </div>
    );
  };

  const renderPastMotionData = (dateKey, deviceKey) => {
    const deviceQuarterlyData = pastDataPerQuarter[dateKey][deviceKey];
    const deviceData = {
      ...pastDataPerHour[dateKey][deviceKey],
      quarterlyData: deviceQuarterlyData?.quarterlyData,
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div className="past-motion-container">{translatedPastMotionTitle(dateKey)}</div>
        {timeArray.map((hour) =>
          renderHours(
            deviceData.model,
            hour,
            deviceData.hourlyData?.hourlyData,
            deviceData.hourlyData?.timeRanges,
            deviceData?.quarterlyData?.quarterlyData,
            deviceData?.quarterlyData?.activityRangesHour,
            lastHourEntry,
            'past',
            {},
            deviceKey
          )
        )}
      </div>
    );
  };

  useEffect(() => {
    if (Object.keys(allDevicesFetched).length) {
      fetchMotionData();
    }
  }, [allDevicesFetched, activityDateStart, activityDateEnd]);

  useEffect(() => {
    if (!accessoriesFetched) return;

    const deviceObject = accessoriesData.reduce((obj, device) => {
      if (device.modelnumber === 'HC200' || device.modelnumber === 'EM200') {
        obj[device.deviceuuid] = {
          serialnumber: device?.device_serial_number,
          modelnumber: device.modelnumber,
          location: device.location,
          battery_level: device?.device_battery_level,
          connectionStatus:
            device.modelnumber === 'HC200'
              ? device.deviceconnectstatus
              : device.paired_status === 'UNPAIRED'
                ? false
                : device.device_connected
                  ? 'CONNECTED'
                  : 'DISCONNECTED',
        };
      }
      return obj;
    }, {});

    if (Object.keys(deviceObject).length === 0) {
      setDataLoaded(true);
      console.error('No user device found with motion activity', 'No Data found.');
    }

    setAllDevicesFetched(deviceObject);
  }, [accessoriesData, accessoriesFetched]);

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const toggleArrowVisibility = (date) => {
    const today = moment().startOf('day');

    const shouldHideRightArrow = moment(date).isSame(today, 'day');
    const shouldHideLeftArrow = moment(date).isSame(today.clone().subtract(1, 'month'), 'day');

    setHideRightArrow(shouldHideRightArrow);
    setHideLeftArrow(shouldHideLeftArrow);
  };

  const onActionClick =
    (date, activityDateStart, activityDateEnd, toggle = false) =>
    () => {
      setDataLoaded(false);
      if (date) {
        toggleArrowVisibility(date);
        setDateToDisplay(date.toDateString());
        setActivityDateStart(activityDateStart);
        setActivityDateEnd(activityDateEnd);
        setShowPastDataDevice([]);
        setPastDataLoaded(false);
      }
      toggle && toggleCalendar();
    };

  const handleArrowClick = (direction) => {
    setDataLoaded(false);
    const currentDate = moment(dateToDisplay);
    const nextDate =
      direction === 'forward'
        ? currentDate.add(1, 'day').toDate()
        : currentDate.subtract(1, 'day').toDate();

    setDateToDisplay(nextDate.toDateString());
    setActivityDateStart(moment(nextDate).startOf('day').format());
    setActivityDateEnd(moment(nextDate).endOf('day').format());
    setShiftDate(direction);
    toggleArrowVisibility(nextDate);
    setShowPastDataDevice([]);
    setPastDataLoaded(false);
  };

  const handleClickOutsideCalendar = (e) => {
    if (calendarRef.current && !calendarRef.current.contains(e.target)) {
      setShowCalendar(false);
    }
  };

  const handleClickPastDataToggle = async (key) => {
    setShowPastDataDevice((prevState) => {
      const isKeyIncluded = prevState.includes(key);
      if (!isKeyIncluded) {
        renderSevenDaysData(key);
      }
      return isKeyIncluded ? prevState.filter((item) => item !== key) : [...prevState, key];
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideCalendar);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideCalendar);
    };
  }, []);

  const translatedDateToDisplay = (dateString) => {
    const parts = dateString.split(' ');
    if (parts.length >= 3) {
      const dayOfWeek = parts[0]; // Thu
      const month = parts[1]; // Jul
      const day = parts[2]; // 24
      const year = parts[3]; // 2025

      const translatedDay = trans(dayOfWeek.toUpperCase());
      const translatedMonth = trans(month.toUpperCase());

      return `${translatedDay} ${translatedMonth} ${day} ${year}`;
    }

    return dateString;
  };

  return (
    <div style={{ fontFamily: 'Euclid Circular' }}>
      {!initialDataLoaded ? (
        <Spin spinning={true} style={{ marginTop: '100px', textAlign: 'center', width: '100%' }}>
          <div style={{ minHeight: '200px' }}></div>
        </Spin>
      ) : (
        <>
          {!isTrend && (
            <div className="motion-activity-header">
              <MotionActivityIcon2 />
              <div className="motion-activity-title">{trans('MOTIONACTIVITY')}</div>
            </div>
          )}
          <div className="motion-calendar-container">
            <div className="motion-calendar-date-display">
              {translatedDateToDisplay(dateToDisplay)}
            </div>
            <div className="calendar-nav-icons">
              <div className="arrow-icons">
                {!hideLeftArrow && (
                  <ArrowLeftIcon
                    className="calendar-nav-icon"
                    onClick={() => handleArrowClick('back')}
                  />
                )}
              </div>
              <button onClick={toggleCalendar} className="button-select-date">
                {showCalendar ? trans('SELECTDATE') : trans('SELECTDATE')}
              </button>
              <div className="arrow-icons">
                {!hideRightArrow && (
                  <ArrowRightIcon
                    className="calendar-nav-icon"
                    onClick={() => handleArrowClick('forward')}
                  />
                )}
              </div>
            </div>
            {showCalendar && (
              <div ref={calendarRef}>
                <MotionCalendar
                  isTrend={isTrend}
                  onActionClick={onActionClick}
                  shiftDate={shiftDate}
                  dateToDisplay={dateToDisplay}
                />
              </div>
            )}
          </div>

          <Spin spinning={!dataLoaded} style={{ marginTop: '100px' }}>
            {dataLoaded ? (
              Object.keys(allDevicesFetched).length > 0 ? (
                sortByModelAndLocation(dataPerHour).map(
                  isTrend ? renderActivityItemTrend : renderActivityItem
                )
              ) : (
                <div
                  className="no-motion-data"
                  style={{
                    textAlign: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginTop: '50px',
                  }}
                >
                  {trans('MOTIONACTIVITYDATAISNOTAVAILABLE')}
                </div>
              )
            ) : null}
          </Spin>
        </>
      )}
    </div>
  );
};

export default MotionActivity;
